import { Grid, useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import * as S from './PinTileGrid.style';
import type { DetailedPinWithChecked } from './AllPins';
import { getPinTile } from '~/modules/design-board/util';
import { DesignBoardPinTargetType } from 'generated/graphql';
import { useMatchesHandleData } from '~/modules/root';
import { useEffect, useRef, useState } from 'react';
import { useDesignBoardPageState } from '~/modules/design-board/component/context';
import UploadTileWithFetcher from '~/modules/design-board/component/upload/UploadTileWithFetcher';
import { useRevalidator } from '@remix-run/react';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useTracker } from '@archipro-website/tracker';
import { useConfigContext } from '@archipro-website/config/bindings/react';

interface PinTileGridProps {
    pins: DetailedPinWithChecked[];
    onUpdate: (pinId: number, checked: boolean) => void;
    topActionDisabled?: boolean;
    targetType?: DesignBoardPinTargetType;
    onDelete: (pinId: number) => void;
    boardId?: number;
    onUploadComplete?: (pins: DetailedPinWithChecked[]) => void;
}

const PinTileGrid = ({
    pins,
    onUpdate,
    onDelete,
    boardId,
    targetType,
    onUploadComplete: onAllUploadComplete,
}: PinTileGridProps) => {
    const isArticleTile = targetType === DesignBoardPinTargetType.Article;
    const { css } = useStyles({ isArticleTile });
    const { mobile } = useAppDisplayModeContext();
    const isAllPinsPage = useMatchesHandleData('isAllPinsPage', false);

    const {
        state: { uploadFiles },
        dispatch,
    } = useDesignBoardPageState();

    const [uploadTimer, setUploadTimer] = useState(0);
    const completeFileCountRef = useRef(0);
    const uploadedPinsRef = useRef<DetailedPinWithChecked[]>([]);
    const isSuccessToastDisplayedRef = useRef(false);
    let revalidator = useRevalidator();
    const { toast } = useToaster();

    const mobileGridColumns =
        targetType === DesignBoardPinTargetType.Product ||
        targetType === DesignBoardPinTargetType.MemberUpload
            ? 2
            : 1;
    const gridColumns = mobile ? mobileGridColumns : 5;

    const showUploadTiles = mobile
        ? targetType === DesignBoardPinTargetType.MemberUpload &&
          uploadFiles.length > 0
        : uploadFiles.length > 0;

    const onOneUploadComplete = (
        message: string,
        pin?: DetailedPinWithChecked
    ) => {
        completeFileCountRef.current += 1;
        if (pin?.Title) {
            uploadedPinsRef.current = [...uploadedPinsRef.current, pin];
            if (!isSuccessToastDisplayedRef.current) {
                isSuccessToastDisplayedRef.current = true;
                toast('Pin added to design board', { type: 'success' });
            }
        }
    };

    if (
        uploadFiles.length > 0 &&
        completeFileCountRef.current > 0 &&
        completeFileCountRef.current >= uploadFiles.length
    ) {
        completeFileCountRef.current = 0;

        const sameSortPins: DetailedPinWithChecked[] = [];
        uploadFiles.map((file) => {
            const selectedFile = file.imagesWithPreview?.[0] || file.files?.[0];
            if (!selectedFile) {
                return undefined;
            }
            const pin = uploadedPinsRef.current.find(
                (pin) => pin.Title === selectedFile.name
            );
            if (pin) {
                sameSortPins.push(pin);
            }
            return pin;
        });

        setTimeout(
            () => {
                dispatch({
                    type: 'UploadPin',
                    uploadFiles: [],
                });
                setUploadTimer(0);
                onAllUploadComplete?.(sameSortPins);
                uploadedPinsRef.current = [];
                isSuccessToastDisplayedRef.current = false;
                mobile && revalidator.revalidate();
            },
            uploadTimer >= 5 ? 0 : (5 - uploadTimer) * 1000
        );
    }

    useEffect(() => {
        let intervalId: ReturnType<typeof setInterval>;
        if (uploadFiles.length > 0) {
            intervalId = setInterval(() => {
                setUploadTimer((prevTimer) => prevTimer + 1);
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [uploadFiles.length]);

    return (
        <Grid
            className={css(mobile ? S.MobileTileGrid : S.DesktopTileGrid)}
            columns={gridColumns}
        >
            {boardId &&
                showUploadTiles &&
                uploadFiles.map((file, index) => {
                    return (
                        <UploadTileWithFetcher
                            key={index}
                            validationResult={file}
                            boardId={boardId}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            onComplete={onOneUploadComplete}
                        />
                    );
                })}

            {pins.map((pin, index) => {
                if (
                    targetType === DesignBoardPinTargetType.MemberUpload &&
                    pin.TargetFilterType !== targetType
                ) {
                    return null;
                }
                return (
                    <PinTile
                        isAllPins={isAllPinsPage}
                        key={`${index}-${pin.ID}`}
                        pin={pin}
                        isMobile={mobile}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        index={index}
                    />
                );
            })}
        </Grid>
    );
};

interface PinTileProps {
    pin: DetailedPinWithChecked;
    isMobile: boolean;
    index: number;
    onUpdate: (pinId: number, checked: boolean) => void;
    onDelete: (pinId: number) => void;
    isAllPins?: boolean;
}

export const PinTile = ({
    pin,
    isMobile,
    index,
    onUpdate,
    onDelete,
    isAllPins,
}: PinTileProps) => {
    const { css } = useStyles({ checked: pin.checked, index });
    const tracker = useTracker();
    const config = useConfigContext();

    const handleDelete = (id: number) => {
        onDelete(id);
        tracker.log('PinMenuDelete', {
            label: pin.TileData?.professionalTitle || '',
            url: new URL(window.location.href),
            data: {
                ItemType: 'pin',
                ItemID: id || 0,
            },
        });
    };

    return (
        <div className={css(S.PinTile)}>
            {getPinTile({
                assetsBaseUrl: config.assetsBaseUrl,
                isChecked: pin.checked,
                pin,
                isMobile,
                index,
                onUpdate,
                onDelete: handleDelete,
                isAllPins,
            })}
        </div>
    );
};

export default PinTileGrid;
